import { parisToday, stockCountCreateSchema } from "@erp/shared";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { ClipboardList, Plus } from "lucide-react";
import { useState, type FormEvent } from "react";
import { toast } from "sonner";
import { Badge } from "~/components/ui/badge";
import { Button } from "~/components/ui/button";
import { DataList, type Column, type SortState } from "~/components/ui/data-list";
import { Field } from "~/components/ui/field";
import { Input } from "~/components/ui/input";
import { Page, PageHeader, Panel } from "~/components/ui/page";
import { Select } from "~/components/ui/select";
import { Sheet } from "~/components/ui/sheet";
import { EmptyState, ErrorState } from "~/components/ui/states";
import { COUNT_TONE, StockTabs } from "~/features/stock/stock";
import { api, unwrap } from "~/lib/api";
import { errorMessageKey } from "~/lib/errors";
import { useFormat, useT } from "~/lib/i18n";
import { useCan } from "~/lib/session";
import { HelpTip } from "~/components/ui/help-tip";

export const Route = createFileRoute("/_app/stock/counts/")({ component: Counts });

type Row = Awaited<ReturnType<typeof fetchCounts>>[number];
const fetchCounts = () => unwrap(api.stock.counts.get());
const ALL = "__all";

function Counts() {
  const t = useT();
  const format = useFormat();
  const can = useCan();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [creating, setCreating] = useState(false);
  const [countedAt, setCountedAt] = useState(() => parisToday());
  const [productType, setProductType] = useState(ALL);
  // At most 200 counts, all loaded: sorted here, newest first by default.
  const [sort, setSort] = useState<SortState>({ key: "date", dir: "desc" });
  const counts = useQuery({ queryKey: ["stock", "counts", "list"], queryFn: fetchCounts });
  // Listed newest count first: the first validated one is the last inventory booked.
  const lastValidated = counts.data?.find((c) => c.status === "validated");
  const types = useQuery({ queryKey: ["products", "types"], queryFn: () => unwrap(api.products.types.get()) });

  const create = useMutation({
    mutationFn: () =>
      unwrap(
        api.stock.counts.post(stockCountCreateSchema.parse({ countedAt, productType: productType === ALL ? null : productType })),
      ),
    onSuccess: (count) => {
      void queryClient.invalidateQueries({ queryKey: ["stock", "counts"] });
      toast.success(t("stock.counts.created"));
      void navigate({ to: "/stock/counts/$id", params: { id: count.id } });
    },
    onError: (error) => toast.error(t(errorMessageKey(error))),
  });

  const columns: Column<Row>[] = [
    {
      id: "number",
      header: t("common.fields.reference"),
      sortKey: "number",
      cell: (r) => <span className="font-medium tabular">{r.number}</span>,
    },
    { id: "date", header: t("stock.counts.countedAt"), sortKey: "date", cell: (r) => format.date(r.countedAt, "long") },
    {
      id: "progress",
      header: t("stock.counts.counted"),
      cell: (r) => t("stock.counts.progress", { counted: r.countedLines, total: r.lineCount }),
      className: "text-fg-2",
    },
    {
      id: "status",
      header: t("common.fields.status"),
      sortKey: "status",
      cell: (r) => <Badge tone={COUNT_TONE[r.status]}>{t(`stock.counts.statuses.${r.status}`)}</Badge>,
    },
  ];

  const newButton = can("stock:write") ? (
    <Button variant="primary" onClick={() => setCreating(true)}>
      <Plus />
      {t("stock.counts.new")}
    </Button>
  ) : null;

  const submit = (event: FormEvent) => {
    event.preventDefault();
    create.mutate();
  };

  return (
    <Page>
      <PageHeader
        title={t("stock.title")}
        meta={
          counts.data
            ? lastValidated
              ? t("stock.counts.lastValidated", { date: format.date(lastValidated.countedAt, "long") })
              : t("stock.counts.noneValidated")
            : null
        }
        actions={
          newButton ? (
            <span className="inline-flex items-center gap-0.5">
              {newButton}
              <HelpTip id="stock.count" />
            </span>
          ) : null
        }
      />
      <StockTabs />
      <Panel>
        {counts.isError ? (
          <ErrorState error={counts.error} onRetry={() => void counts.refetch()} />
        ) : (
          <DataList
            rows={sortCounts(counts.data ?? [], sort)}
            rowKey={(r) => r.id}
            columns={columns}
            card={(r) => (
              <div className="flex items-center justify-between gap-3">
                <div>
                  <p className="font-medium tabular">{r.number}</p>
                  <p className="text-sm text-fg-2">{format.date(r.countedAt, "long")}</p>
                </div>
                <Badge tone={COUNT_TONE[r.status]}>{t(`stock.counts.statuses.${r.status}`)}</Badge>
              </div>
            )}
            onOpen={(r) => void navigate({ to: "/stock/counts/$id", params: { id: r.id } })}
            sort={sort}
            onSortChange={setSort}
            loading={counts.isPending}
            hasMore={false}
            loadingMore={false}
            onLoadMore={() => undefined}
            isDimmed={(r) => r.status === "cancelled"}
            empty={
              <EmptyState
                icon={<ClipboardList />}
                title={t("stock.counts.emptyTitle")}
                body={t("stock.counts.emptyBody")}
                action={newButton}
              />
            }
          />
        )}
      </Panel>

      <Sheet
        open={creating}
        onOpenChange={setCreating}
        title={t("stock.counts.new")}
        description={t("stock.counts.newHint")}
        footer={
          <>
            <Button variant="ghost" onClick={() => setCreating(false)}>
              {t("common.actions.cancel")}
            </Button>
            <Button variant="primary" type="submit" form="count-form" loading={create.isPending}>
              {t("common.actions.create")}
            </Button>
          </>
        }
      >
        <form id="count-form" onSubmit={submit} className="flex flex-col gap-4" noValidate>
          <Field label={t("stock.counts.countedAt")}>
            <Input type="date" value={countedAt} onChange={(e) => setCountedAt(e.target.value)} max={parisToday()} />
          </Field>
          <Field label={t("stock.counts.scope")}>
            <Select
              value={productType}
              onValueChange={setProductType}
              options={[
                { value: ALL, label: t("stock.counts.allProducts") },
                ...(types.data ?? []).map((v) => ({ value: v, label: v })),
              ]}
            />
          </Field>
        </form>
      </Sheet>
    </Page>
  );
}

/** Counts in the order the list header asks for (date, reference or status), the newest breaking ties. */
function sortCounts<R extends { number: string; status: string; countedAt: string | Date }>(rows: R[], sort: SortState): R[] {
  const time = (r: R) => new Date(r.countedAt).getTime();
  const value = (r: R): number | string => (sort.key === "number" ? r.number : sort.key === "status" ? r.status : time(r));
  const sign = sort.dir === "asc" ? 1 : -1;
  return rows.toSorted((a, b) => {
    const [x, y] = [value(a), value(b)];
    return (x < y ? -1 : x > y ? 1 : 0) * sign || time(b) - time(a);
  });
}
